import { Component } from '@theme/component';
import {
  supportsViewTransitions,
  startViewTransition,
  onAnimationEnd,
  prefersReducedMotion,
  debounce,
  preloadImage,
  isLowPowerDevice,
} from '@theme/utilities';
import { scrollIntoView } from '@theme/scrolling';
import { ZoomMediaSelectedEvent } from '@theme/events';
import { DialogCloseEvent } from '@theme/dialog';
/**
 * A custom element that renders a zoom dialog.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The dialog element.
 * @property {HTMLElement[]} media - The media elements.
 * @property {HTMLElement} thumbnails - The thumbnails elements.
 *
 * @extends Component<Refs>
 */
export class ZoomDialog extends Component {
  requiredRefs = ['dialog', 'media', 'thumbnails'];

  #highResImagesLoaded = /** @type {Set<string>} */ (new Set());

  connectedCallback() {
    super.connectedCallback();
    this.refs.dialog?.addEventListener('scroll', this.handleScroll);
    const zoomList = this.refs.media?.[0]?.parentElement;
    if (zoomList) {
      zoomList.addEventListener('scroll', this.handleScroll);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.refs.dialog?.removeEventListener('scroll', this.handleScroll);
    const zoomList = this.refs.media?.[0]?.parentElement;
    if (zoomList) {
      zoomList.removeEventListener('scroll', this.handleScroll);
    }
  }

  /**
   * Opens the zoom dialog.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} [event] - The pointer event.
   */
  async open(index, event) {
    if (event) event.preventDefault();

    const { dialog, media, thumbnails } = this.refs;
    if (!dialog) return;

    const targetImage = media?.[index];
    const targetThumbnail = thumbnails?.children?.[index];

    const open = () => {
      dialog.showModal();

      for (const target of [targetThumbnail, targetImage]) {
        target?.scrollIntoView({ behavior: 'instant' });
      }
      this.updateCaption(index);
    };

    /** @type {HTMLElement | null} */
    const sourceImage = event?.target instanceof Element ? event.target.closest('li,slideshow-slide') : null;

    if (!supportsViewTransitions() || isLowPowerDevice() || !sourceImage || !targetImage) return open();

    const itemTransitionName = `gallery-item-open`;
    sourceImage.style.setProperty('view-transition-name', itemTransitionName);

    const focalPoint = sourceImage.dataset.focalPoint;
    if (focalPoint) {
      document.documentElement.style.setProperty('--gallery-media-focal-point', focalPoint);
    }

    await startViewTransition(() => {
      open();
      sourceImage.style.removeProperty('view-transition-name');
      targetImage.style.setProperty('view-transition-name', itemTransitionName);
    });

    document.documentElement.style.removeProperty('--gallery-media-focal-point');
    targetImage.style.removeProperty('view-transition-name');

    this.selectThumbnail(index, { behavior: 'instant' });
    this.updateCaption(index);
  }

  /**
   * Gets the currently active media index.
   * @returns {number}
   */
  getActiveIndex() {
    const { thumbnails, media } = this.refs;
    if (thumbnails) {
      const selectedButton = thumbnails.querySelector('button[aria-selected="true"]');
      if (selectedButton) {
        const buttons = Array.from(thumbnails.querySelectorAll('button'));
        const idx = buttons.indexOf(selectedButton);
        if (idx !== -1) return idx;
      }
    }
    if (media && media.length) {
      const container = media[0]?.parentElement;
      if (container) {
        const scrollLeft = container.scrollLeft;
        const width = container.clientWidth || window.innerWidth;
        if (width > 0) {
          return Math.round(scrollLeft / width);
        }
      }
    }
    return 0;
  }

  /**
   * Navigates to the next media item.
   */
  next() {
    const { media } = this.refs;
    if (!media || !media.length) return;
    const currentIndex = this.getActiveIndex();
    const nextIndex = (currentIndex + 1) % media.length;
    this.selectThumbnail(nextIndex);
  }

  /**
   * Navigates to the previous media item.
   */
  prev() {
    const { media } = this.refs;
    if (!media || !media.length) return;
    const currentIndex = this.getActiveIndex();
    const prevIndex = (currentIndex - 1 + media.length) % media.length;
    this.selectThumbnail(prevIndex);
  }

  /**
   * Updates the blurred background image for the currently active media item.
   * @param {number} index
   */
  updateBgImage(index) {
    if (!this.refs.bgImage) return;
    const { media } = this.refs;
    if (!media || !media[index]) return;
    const activeMedia = media[index];
    const img = activeMedia.querySelector('img');
    if (img && img.src) {
      this.refs.bgImage.src = img.src;
    }
  }

  /**
   * Updates the caption text display for the currently active media item.
   * @param {number} index
   */
  updateCaption(index) {
    this.updateBgImage(index);
    if (!this.refs.caption) return;
    const { media } = this.refs;
    if (!media || !media[index]) return;
    const activeMedia = media[index];
    const captionText = activeMedia.getAttribute('data-caption') || activeMedia.dataset.alt || '';
    this.refs.caption.textContent = captionText;
  }

  /**
   * Loads a high-resolution image for a specific media container
   * @param {HTMLElement} mediaContainer - The media container element
   */
  loadHighResolutionImage(mediaContainer) {
    if (!mediaContainer || !mediaContainer.classList.contains('product-media-container--image')) return false;

    const image = mediaContainer.querySelector('img.product-media__image');
    if (!image || !(image instanceof HTMLImageElement)) return false;

    const highResolutionUrl = image.getAttribute('data_max_resolution');
    if (!highResolutionUrl || this.#highResImagesLoaded.has(highResolutionUrl)) return false;

    preloadImage(highResolutionUrl);

    const newImage = new Image();
    newImage.className = image.className;
    newImage.alt = image.alt;
    newImage.setAttribute('data_max_resolution', highResolutionUrl);
    newImage.setAttribute('ref', 'image');

    // When the high-resolution image loads, replace the existing image
    newImage.onload = () => {
      image.replaceWith(newImage);
      this.#highResImagesLoaded.add(highResolutionUrl);
    };

    newImage.src = highResolutionUrl;
  }

  /**
   * Handles the scroll event of the dialog, which is used to update the active thumbnail when the corresponding image is visible in the main view.
   * @param {Event} event - The scroll event.
   */
  handleScroll = debounce(async () => {
    const { media, thumbnails } = this.refs;
    if (!media || !media.length) return;

    const mostVisibleElement = await getMostVisibleElement(media);
    const activeIndex = media.indexOf(mostVisibleElement);

    if (thumbnails && thumbnails.children[activeIndex]) {
      Array.from(thumbnails.querySelectorAll('button')).forEach((button, i) => {
        button.setAttribute('aria-selected', `${i === activeIndex}`);
      });
    }

    if (mostVisibleElement) {
      this.loadHighResolutionImage(mostVisibleElement);
    }
    this.updateCaption(activeIndex);
    this.dispatchEvent(new ZoomMediaSelectedEvent(activeIndex));
  }, 50);

  /**
   * Closes the zoom dialog.
   */
  async close() {
    const { dialog, media } = this.refs;
    if (!dialog) return;

    if (!supportsViewTransitions() || isLowPowerDevice() || !media) return this.closeDialog();

    // Find the most visible image using IntersectionObserver
    const mostVisibleElement = await getMostVisibleElement(media);

    // Get the index and set up transition
    const activeIndex = media.indexOf(mostVisibleElement);
    const itemTransitionName = `gallery-item-close`;

    const mediaGallery = /** @type {import('./media-gallery').MediaGallery | undefined} */ (
      this.closest('media-gallery')
    );

    const slideshowActive = mediaGallery?.presentation === 'carousel';

    const slide = slideshowActive ? mediaGallery.slideshow?.slides?.[activeIndex] : mediaGallery?.media?.[activeIndex];

    if (!slide) return this.closeDialog();
    const focalPoint = slide.dataset.focalPoint;
    if (focalPoint) {
      document.documentElement.style.setProperty('--gallery-media-focal-point', focalPoint);
    }

    dialog.classList.add('dialog--closed');

    if (this.refs.thumbnails) {
      await Promise.race([
        onAnimationEnd(this.refs.thumbnails),
        new Promise((resolve) => setTimeout(resolve, 300)),
      ]);
    }

    mostVisibleElement.style.setProperty('view-transition-name', itemTransitionName);

    await startViewTransition(() => {
      mostVisibleElement.style.removeProperty('view-transition-name');
      slide.style.setProperty('view-transition-name', itemTransitionName);
      this.closeDialog();
    });

    slide.style.removeProperty('view-transition-name');
    dialog.classList.remove('dialog--closed');
    document.documentElement.style.removeProperty('--gallery-media-focal-point');
  }

  closeDialog() {
    const { dialog } = this.refs;
    if (dialog && typeof dialog.close === 'function') {
      dialog.close();
    }
    window.dispatchEvent(new DialogCloseEvent());
  }

  /**
   * Handles the keydown event of the dialog.
   *
   * @param {KeyboardEvent} event - The keyboard event.
   */
  handleKeyDown(event) {
    if (event.key === 'Escape') {
      event.preventDefault();
      this.close();
    } else if (event.key === 'ArrowRight') {
      event.preventDefault();
      this.next();
    } else if (event.key === 'ArrowLeft') {
      event.preventDefault();
      this.prev();
    }
  }

  /**
   * Handles the click event of a thumbnail.
   * @param {number} index - The index of the thumbnail to select.
   */
  async handleThumbnailClick(index) {
    const behavior = prefersReducedMotion() ? 'instant' : 'smooth';
    this.selectThumbnail(index, { behavior });
  }

  /**
   * Handles the pointer enter event of a thumbnail.
   * @param {number} index - The index of the thumbnail to load the high-resolution image for.
   */
  async handleThumbnailPointerEnter(index) {
    const { media } = this.refs;
    if (!media || !media[index]) return;

    this.loadHighResolutionImage(media[index]);
  }

  /**
   * Handles the selection of a thumbnail.
   * @param {number} index - The index of the thumbnail to select.
   * @param {Object} options - The options for the selection.
   * @param {ScrollBehavior} options.behavior - The behavior of the scroll.
   */
  async selectThumbnail(index, options = { behavior: 'smooth' }) {
    const { media, thumbnails } = this.refs;

    if (thumbnails && thumbnails.children.length) {
      if (!isNaN(index) && index >= 0 && index < thumbnails.children.length) {
        const targetThumbnail = thumbnails.children[index];
        if (targetThumbnail && targetThumbnail instanceof HTMLElement) {
          Array.from(thumbnails.querySelectorAll('button')).forEach((button, i) => {
            button.setAttribute('aria-selected', `${i === index}`);
          });

          scrollIntoView(targetThumbnail, {
            ancestor: thumbnails,
            behavior: options.behavior,
            block: 'center',
            inline: 'center',
          });
        }
      }
    }

    if (media && media[index]) {
      const targetImage = media[index];
      targetImage.scrollIntoView({
        behavior: options.behavior,
      });

      this.loadHighResolutionImage(targetImage);
      this.updateCaption(index);
    }
    this.dispatchEvent(new ZoomMediaSelectedEvent(index));
  }
}

if (!customElements.get('zoom-dialog')) {
  customElements.define('zoom-dialog', ZoomDialog);
}

/**
 * Get the most visible element from a list of elements.
 * @param {HTMLElement[]} elements - The elements to get the most visible element from.
 * @returns {Promise<HTMLElement>} A promise that resolves to the most visible element.
 */
function getMostVisibleElement(elements) {
  return new Promise((resolve) => {
    const observer = new IntersectionObserver(
      (entries) => {
        const mostVisible = entries.reduce((prev, current) =>
          current.intersectionRatio > prev.intersectionRatio ? current : prev
        );
        observer.disconnect();
        resolve(/** @type {HTMLElement} */ (mostVisible.target));
      },
      {
        threshold: Array.from({ length: 100 }, (_, i) => i / 100),
      }
    );

    for (const element of elements) {
      observer.observe(element);
    }
  });
}
